import classNames from 'classnames';
import styles from './Header.module.scss';
import { UI_SkipToMain } from 'ui/components/1-atoms/SkipToMain/SkipToMain';
import { UI_Container } from 'ui/components/4-habitats/Container/Container';
import { UI_Grid } from 'ui/components/4-habitats/Grid/Grid';
import { UI_GridCell } from 'ui/components/4-habitats/Grid/GridCell';
import { KeyboardEventHandler, useEffect, useRef, useState } from 'react';
import { disableBodyScroll, enableBodyScroll } from 'body-scroll-lock/lib/bodyScrollLock';
import { throttle } from 'helpers/throttle';

/** Scroll offset past which the header becomes opaque and drops its light colour scheme. */
const SCROLL_THRESHOLD = 500;
const SCROLL_THROTTLE_MS = 200;

export interface UI_HeaderProps {
	className?: string;
	logoArea?: React.ReactNode;
	cartArea?: React.ReactNode;
	children?: React.ReactNode;
	languageSelector?: React.ReactNode;
	toggled?: boolean;
	lightHeader?: boolean;
	toggleMenu?: () => void;
	menuLabel?: string;
	closeMenuLabel?: string;
	skipToContentLabel?: string;
	skipToContentTitle?: string;
	closeMenuRef?: React.Ref<HTMLLabelElement>;
	isTransitioning?: boolean;
	routeKey?: string;
}

export const UI_Header: React.FC<UI_HeaderProps> = ({
	className,
	children,
	logoArea,
	toggled = false,
	lightHeader,
	toggleMenu,
	menuLabel,
	closeMenuLabel,
	skipToContentLabel,
	skipToContentTitle,
	closeMenuRef,
	isTransitioning = true,
	routeKey,
}) => {
	// Single source of truth for scroll position. Everything colour-related is derived
	// from it at render time, so closing the menu can never leave stale colour state behind.
	const [isScrolled, setIsScrolled] = useState(false);
	const [isActive, setIsActive] = useState(true);
	const headerRef = useRef<HTMLDivElement>(null);

	const isTransparent = !isScrolled;
	const headerIsLight = Boolean(lightHeader) && !isScrolled;

	const handleKeyDown: KeyboardEventHandler<HTMLLabelElement> = (event) => {
		if (event.key === 'Enter') {
			toggleMenu?.();
		}
	};

	useEffect(() => {
		if (!toggled) {
			return;
		}

		const handleEscape = (event: KeyboardEvent) => {
			if (event.key === 'Escape') {
				toggleMenu?.();
			}
		};

		document.addEventListener('keydown', handleEscape);

		return () => {
			document.removeEventListener('keydown', handleEscape);
		};
	}, [toggled, toggleMenu]);

	// routeKey changes on every route change, forcing a recheck of scroll-based state on navigation.
	useEffect(() => {
		let prevScrollPos = window.scrollY;

		const onScroll = throttle(() => {
			const currentScrollPos = window.scrollY;

			// Transparency and colour depend only on where we are RIGHT NOW. Branching on
			// prevScrollPos made every decision lag one throttle tick, so the final tick after
			// scrolling stopped settled on the previous tick's position and stayed there.
			setIsScrolled(currentScrollPos >= SCROLL_THRESHOLD);

			// Show/hide is a direction question, so this one legitimately compares prev to current.
			setIsActive(prevScrollPos < 0 || prevScrollPos > currentScrollPos);

			prevScrollPos = currentScrollPos;
		}, SCROLL_THROTTLE_MS);

		document.addEventListener('scroll', onScroll);

		setIsScrolled(prevScrollPos >= SCROLL_THRESHOLD);
		setIsActive(true);

		return () => {
			document.removeEventListener('scroll', onScroll);
		};
	}, [routeKey]);

	// Body scroll lock is keyed on `toggled` alone. Previously this lived in the scroll effect,
	// whose deps never included `toggled`, so `disableBodyScroll` was never actually called and
	// the document stayed scrollable behind the open overlay.
	useEffect(() => {
		const headerElement = headerRef.current;

		if (!toggled || !headerElement) {
			return;
		}

		disableBodyScroll(headerElement, { reserveScrollBarGap: true });

		return () => {
			enableBodyScroll(headerElement);
		};
	}, [toggled]);

	return (
		<header
			ref={headerRef}
			className={classNames(
				styles.Header,
				{ [styles.Header___transparent]: isTransparent },
				{ [styles.Header___toggled]: toggled },
				{ [styles.Header___light]: headerIsLight },
				{ [styles.Header___hidden]: !isActive && !toggled && !isTransitioning },
				className,
			)}
			role="banner"
			aria-expanded={toggled}
		>
			<UI_SkipToMain link="#main" title={skipToContentTitle}>
				{skipToContentLabel}
			</UI_SkipToMain>
			<UI_Container width="Standard">
				<UI_Grid container wrap={true} center={true}>
					<UI_GridCell className={styles.Header_identity} mobileWidth="50" desktopWidth="50">
						{logoArea}
					</UI_GridCell>
					{children && (
						<UI_GridCell className={styles.Header_close} mobileWidth="50" desktopWidth="50">
							<input
								className={styles.Header_navController}
								type="checkbox"
								id="nav-controller"
								data-ref="header__navController"
								defaultChecked={toggled}
							/>

							<label
								ref={closeMenuRef}
								className={styles.Header_navToggle}
								htmlFor="nav-controller"
								onClick={toggleMenu}
								onKeyDown={handleKeyDown}
								tabIndex={0}
							>
								{!toggled ? menuLabel : closeMenuLabel}
							</label>
						</UI_GridCell>
					)}
					<UI_GridCell className={styles.Header_navContainer} mobileWidth="100" desktopWidth="100">
						<div className={classNames(styles.Header_navigation)}>
							{children && toggled && (
								<>
									<div className={styles.Header_nav} data-ref="header__navPanel" onClick={toggleMenu}>
										<UI_Grid container wrap={true}>
											<UI_GridCell>{children}</UI_GridCell>
										</UI_Grid>
									</div>
								</>
							)}
						</div>
					</UI_GridCell>
				</UI_Grid>
			</UI_Container>
		</header>
	);
};
